import React, { FC, useMemo } from 'react';
import { Box, Card as MuiCard, styled } from '@mui/material';
import {
  Cell,
  ReferenceArea,
  ReferenceLine,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTheme } from '@mui/material';

import { SkeletonWrapper } from '../SkeletonWrapper/SkeletonWrapper';
import { Typography } from '../Typography/Typography';

import { YAxisBaselineComponent } from './YAxisBaseline';
import { YAxisLimitsComponent } from './YAxisLimits';

const Card = styled(MuiCard)(({ theme }) => ({
  padding: '16px 16px 16px 0',
  ':hover': {
    backgroundColor: theme.palette.background.paper,
  },
}));

interface GraphData {
  x: string;
  y: string;
  color: string;
  extraInfo?: string;
}

interface GraphBasicProps {
  data: GraphData[];
  showSkeleton?: boolean;
  showCard?: boolean;
  extraAxis?: React.ReactNode;
}

interface GraphBaselineProps extends GraphBasicProps {
  baseline: number;
  target: number;
}

interface GraphLimitsProps extends GraphBasicProps {
  limits: { bottom: number; upper: number };
  yAxisUpperLimit?: number;
}

interface ClickableGraphData extends GraphData {
  clicked: boolean;
}

interface GraphClickableProps extends GraphLimitsProps {
  onClick: (index: number) => void;
  clickedIndexes: number[];
  data: ClickableGraphData[];
}

export type GraphProps =
  | GraphBaselineProps
  | GraphLimitsProps
  | GraphClickableProps;

export const Graph: FC<GraphProps> = (props) => {
  const theme = useTheme();

  const { data, showSkeleton, showCard, extraAxis } = props;
  const baseline = 'baseline' in props ? props.baseline : undefined;
  const target = 'target' in props ? props.target : undefined;
  const limits = 'limits' in props ? props.limits : undefined;
  const yAxisUpperLimit =
    'yAxisUpperLimit' in props ? props.yAxisUpperLimit : undefined;
  const onClick = 'onClick' in props ? props.onClick : undefined;

  const modifiedData = useMemo(() => {
    return data.map((point) => ({
      ...point,
      displayY: yAxisUpperLimit
        ? Math.min(parseFloat(point.y), yAxisUpperLimit)
        : point.y,
      originalY: point.y,
    }));
  }, [data, yAxisUpperLimit]);

  const ticks = useMemo(() => {
    if (limits) {
      return [0.1, 2, 6.5, limits.bottom, limits.upper]
        .sort((a, b) => a - b)
        .map((l) => l.toFixed(1));
    }

    if (baseline && target) {
      const minDataValue = Math.min(
        ...data.map((d) => parseFloat(d.y)),
        target
      );

      const maxDataValue = Math.max(
        ...data.map((d) => parseFloat(d.y)),
        baseline
      );

      const min = minDataValue - minDataValue * 0.5;
      const max = maxDataValue + maxDataValue * 0.5;

      const range = max - min;
      const tickIndex = range / 6;

      const multiples: string[] = [];
      let previousValue = min - tickIndex;

      for (let i = 0; i <= 6; i++) {
        const value = previousValue + tickIndex;
        multiples.push(value.toFixed(0));
        previousValue = value;
      }

      return multiples;
    }

    return [];
  }, [limits, baseline, target, data]);

  const renderGraph = () => (
    <Box
      display="flex"
      flexDirection="row"
      paddingTop={2.5}
      paddingLeft={1.5}
      paddingRight={4}
    >
      {limits ? (
        <YAxisLimitsComponent ticks={ticks} />
      ) : (
        <YAxisBaselineComponent ticks={ticks} />
      )}
      <Box
        sx={{
          overflowX: 'scroll',
          direction: 'rtl',
          overflowY: 'hidden',
        }}
      >
        <ScatterChart
          width={Math.max(400, 83 * data.length)}
          height={extraAxis ? 236 : 200}
          data={modifiedData}
        >
          <XAxis
            dataKey="x"
            fontSize={12}
            interval="preserveStartEnd"
            allowDuplicatedCategory={false}
          />
          {extraAxis ?? null}
          <YAxis
            dataKey="displayY"
            domain={[Number(ticks[0]), Number(ticks[ticks.length - 1])]}
            hide={true}
            ticks={ticks}
          />
          <Tooltip
            cursor={false}
            content={(props) => {
              const { payload, active } = props;
              if (!active || !payload || !payload[0]?.payload) {
                return null;
              }
              const value = payload[0].payload.originalY;
              const extraInfo = payload[0].payload.extraInfo;
              const color = payload[0].payload.color;

              return (
                <Box
                  sx={{
                    backgroundColor: onClick ? theme.palette.text.hint : color,
                    borderRadius: 20,
                    minWidth: 35,
                    minHeight: 23,
                    paddingInline: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    color={theme.palette.background.paper}
                    dir="ltr"
                  >
                    {onClick ? (
                      'View details'
                    ) : (
                      <>
                        <b>{value}</b> {extraInfo}
                      </>
                    )}
                  </Typography>
                </Box>
              );
            }}
          />
          {baseline && (
            <ReferenceLine y={baseline} stroke="#B72D3B" width={2} />
          )}
          {target && data.length && (
            <>
              <ReferenceLine y={target} stroke="#3CA130" width={2} />
              <ReferenceArea
                x1={data[0].x}
                x2={data[data.length - 1].x}
                y1={ticks[0]}
                y2={target}
                fill="rgba(0, 73, 51, 0.08)"
              />
            </>
          )}
          {limits && (
            <>
              {Object.values(limits).map((limit, index) => (
                <ReferenceLine
                  key={index}
                  y={limit}
                  stroke="#3CA130"
                  strokeWidth={2}
                />
              ))}
              <ReferenceArea
                y1={limits.bottom}
                y2={limits.upper}
                fill="rgba(0, 73, 51, 0.08)"
              />
            </>
          )}
          <Scatter
            fill={theme.palette.text.hint}
            line
            type="monotone"
            dataKey="displayY"
            onClick={
              onClick
                ? (_data, index) => {
                    onClick(index);
                  }
                : undefined
            }
          >
            {data.map((entry, index) => (
              <Cell
                key={index}
                fill={
                  !onClick
                    ? theme.palette.text.hint
                    : 'clicked' in entry && entry.clicked
                    ? theme.palette.primary.main
                    : theme.palette.background.paper
                }
                strokeWidth={onClick ? 2 : 0}
                stroke={theme.palette.primary.main}
                style={onClick ? { cursor: 'pointer' } : undefined}
              />
            ))}
          </Scatter>
        </ScatterChart>
      </Box>
    </Box>
  );

  return (
    <SkeletonWrapper isLoading={!!showSkeleton}>
      {showCard ? <Card>{renderGraph()}</Card> : renderGraph()}
    </SkeletonWrapper>
  );
};
